import { useMemo, useEffect } from 'react';
import zxcvbn from 'zxcvbn';
import { CheckCircle2, XCircle } from 'lucide-react';

interface Props {
  password: string;
  userInputs?: string[];
  minScore?: number;
  minLength?: number;
  onPolicyChange?: (meets: boolean) => void;
}

const SCORE_LABEL = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const;
const SCORE_BAR_CLASS = [
  'bg-danger-500',
  'bg-danger-400',
  'bg-warning-400',
  'bg-success-400',
  'bg-success-500',
] as const;

const BANNED_TOKENS = [
  'dreamit', 'dreamit2', 'zertain', 'caspio', 'password',
  'qwerty', 'clinical', 'trial', 'hipaa',
];

function hasBannedWord(pw: string): string | null {
  const lower = pw.toLowerCase();
  for (const t of BANNED_TOKENS) {
    if (lower.includes(t)) return t;
  }
  return null;
}

export function PasswordStrengthMeter({
  password,
  userInputs = [],
  minScore = 3,
  minLength = 12,
  onPolicyChange,
}: Props) {
  const result = useMemo(() => {
    if (!password) return null;
    try {
      return zxcvbn(password, userInputs.filter(Boolean));
    } catch {
      return null;
    }
  }, [password, userInputs]);

  const lengthOk = password.length >= minLength;
  const bannedWord = password ? hasBannedWord(password) : null;
  const noBanned = !bannedWord;
  const score = result?.score ?? 0;
  const strengthOk = score >= minScore;
  const meetsPolicy = lengthOk && noBanned && strengthOk;

  useEffect(() => {
    onPolicyChange?.(meetsPolicy);
  }, [meetsPolicy, onPolicyChange]);

  if (!password) {
    return (
      <div className="mt-2 space-y-1">
        <p className="text-xs font-medium text-ink-secondary">Password must:</p>
        <ul className="space-y-0.5 text-xs text-ink-muted">
          <li>• Be at least {minLength} characters</li>
          <li>• Score "Strong" or better (avoid names, dates, common words)</li>
          <li>• Not contain words like "password", "clinical", or your organisation name</li>
        </ul>
      </div>
    );
  }

  const feedback = result?.feedback?.warning ?? '';
  const suggestion = result?.feedback?.suggestions?.[0] ?? '';

  return (
    <div className="mt-2 space-y-2">
      <div className="flex h-1.5 gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-full flex-1 rounded ${
              (strengthOk || i <= score) ? SCORE_BAR_CLASS[score] : 'bg-surface-border'
            }`}
          />
        ))}
      </div>

      <div className="space-y-1">
        <Rule ok={lengthOk} label={`At least ${minLength} characters`} />
        <Rule ok={noBanned} label={bannedWord ? `Cannot contain "${bannedWord}"` : 'No restricted words'} />
        <Rule
          ok={strengthOk}
          label={strengthOk ? `${SCORE_LABEL[score]} — meets policy` : `${SCORE_LABEL[score]} — needs ${SCORE_LABEL[minScore]}+`}
        />
      </div>

      {!strengthOk && (feedback || suggestion) && (
        <p className="text-xs text-ink-muted">
          {feedback}
          {feedback && suggestion ? ' — ' : ''}
          {suggestion}
        </p>
      )}
    </div>
  );
}

function Rule({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs ${ok ? 'text-success-700' : 'text-ink-secondary'}`}>
      {ok
        ? <CheckCircle2 size={12} className="shrink-0 text-success-600" />
        : <XCircle size={12} className="shrink-0 text-ink-muted" />
      }
      {label}
    </div>
  );
}
